/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { observer } from 'mobx-react-lite';
import { HttpResponseCodes } from '@veeam-vspc/core';
import { LoginKeyIcon, AlternativeButton, LoginStartForm, immediatelyValidateFlag } from '@veeam-vspc/components';

import type { TokenManagerErrorResponse, TokenManagerResponse, BaseFailedRequestResponse } from '@veeam-vspc/core';
import type { LoginStartFormState } from '@veeam-vspc/components';


import { useAppServices } from 'views/providers/AppProvider/hooks';
import { useLang } from 'views/providers/LangProvider/hooks';
import { useLoginStore } from '../../hooks';
import { BaseFactor } from '../BaseFactor';

import type { LoginStartFactor } from '../../interfaces';

export const LoginStart = observer(() => {
    const { tokenManagerService } = useAppServices();
    const lang = useLang();
    const loginStore = useLoginStore();
    const { identityProviders } = loginStore.currentFactor as LoginStartFactor;
    const goToSsoButton = () => (
        <AlternativeButton
            iconBefore={<LoginKeyIcon />}
            disabled={loginStore.isLoading}
            // onClick={() => loginStore.onFinishFactorHandler({ goToSso: true })} // Uncomment for full SSO
            onClick={() => loginStore.onSsoClick()}
        >
            {lang.LOG_IN_WITH_SSO}
        </AlternativeButton>
    );

    return (
        <BaseFactor alternativeButton={(identityProviders && identityProviders.length > 0) && goToSsoButton()}>
            <LoginStartForm
                title={lang.PLEASE_LOG_IN}
                error={loginStore.error}
                warning={loginStore.warning}
                info={loginStore.info}
                loading={loginStore.isLoading}
                onChange={() => loginStore.resetMessages()}
                logIn={({ login, password }: LoginStartFormState) => {
                    const trimmedLogin = login.trim();
                    loginStore.isLoading = true;

                    tokenManagerService.login(trimmedLogin, password)
                        .then((resp: TokenManagerResponse) => loginStore.onFinishFactorHandler({ ...resp, login: trimmedLogin }))
                        .catch((err: TokenManagerErrorResponse | BaseFailedRequestResponse) => {
                            const { errors } = err as BaseFailedRequestResponse;
                            const throttlingError = errors?.[0]?.code === HttpResponseCodes.TooManyRequests
                                ? lang.CANNOT_COMPLETE_THE_LOGIN_OPERATION
                                : null;
                            const { error, errorDescription } = err as TokenManagerErrorResponse;
                            const isInvalidRequest = error === 'invalid_request';

                            loginStore.isLoading = false;
                            loginStore.error = isInvalidRequest
                                ? lang.FAILED_TO_AUTHENTICATE_A_USER_ACCOUNT
                                : errorDescription || throttlingError || lang.FAILED_TO_AUTHENTICATE_A_USER_ACCOUNT;
                        });
                }}
                texts={{
                    placeholderLogin: lang.USERNAME_TEXTFIELD_PLACEHOLDER,
                    placeholderPassword: lang.ENTER_PASSWORD,
                    actionLogIn: lang.LOG_IN,
                    actionForgotPassword: lang.FORGOT_PASSWORD,
                    errorRequired: lang.THE_FIELD_IS_REQUIRED,
                    errorMaxLength: lang.THE_MAXIMUM_LENGTH_IS,
                    warningCapsLockOn: lang.CAPS_LOCK_IS_ON,
                }}
                onForgotPasswordClick={() => loginStore.onFinishFactorHandler({ goToResetPassword: true })}
                customLoginValidation={(value: string) => {
                    if (value.indexOf('@') !== -1) {
                        return `${lang.SPECIFIED_USERNAME_FORMAT_IS_NOT_SUPPORTED}${immediatelyValidateFlag}`;
                    }
                }}
            />
        </BaseFactor>
    );
});
